import {
  DASHBOARD_OVERVIEW_MERGE_KEYS,
  getDashboardWidgetKeysForRole,
  isFeatureDocKeyAllowedForRole,
  isLeftoverFeatureDocKey,
  resolveFeatureDocKeyForRole,
} from "./abacPermissionUtils";

export function getAbacValueAtPath(node, path) {
  if (!path?.length) return node;
  return path.reduce(
    (current, key) =>
      current && typeof current === "object" ? current[key] : undefined,
    node
  );
}

export function hasAbacPermission(permissions, path, options = {}) {
  const { defaultAllow = false } = options;
  if (!permissions || typeof permissions !== "object") return defaultAllow;

  const value = getAbacValueAtPath(permissions, path);
  if (typeof value === "boolean") return value;
  if (value && typeof value === "object" && typeof value.view === "boolean") {
    return value.view;
  }
  return defaultAllow;
}

export function isTruthyFlag(value) {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
}

function findMultiUserAccessValue(node, depth = 0) {
  if (!node || typeof node !== "object" || depth > 6) return undefined;
  if (Object.prototype.hasOwnProperty.call(node, "multiUserAccess")) {
    return node.multiUserAccess;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findMultiUserAccessValue(item, depth + 1);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === "object") {
      const found = findMultiUserAccessValue(value, depth + 1);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}


export function getMultiUserAccessTriState(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const direct = [
      source.multiUserAccess,
      source.webMasterEntry?.multiUserAccess,
      source.webmasterEntry?.multiUserAccess,
      source.userEntry?.webMasterEntry?.multiUserAccess,
      source.userEntry?.multiUserAccess,
      source.configuration?.multiUserAccess,
    ];
    for (const candidate of direct) {
      if (candidate !== undefined && candidate !== null && candidate !== "") {
        return isTruthyFlag(candidate);
      }
    }
    const deep = findMultiUserAccessValue(source);
    if (deep !== undefined && deep !== null && deep !== "") {
      return isTruthyFlag(deep);
    }
  }
  return null;
}

export function getMultiUserAccessFlag(...sources) {
  return getMultiUserAccessTriState(...sources) === true;
}


export function isMultiAccessUser(user, _profile) {
  if (!user || typeof user !== "object") return false;
  if (user.isMultiAccessMember === true) return true;

  const accessName = String(user.accessName ?? user.accessname ?? "").trim();
  if (accessName) return true;

  return false;
}

export function resolveAbacChannelKey(channel) {
  const key = String(channel || "")
    .trim()
    .toLowerCase();
  if (key === "pushnotification" || key === "push-notification") return "push";
  return key;
}


function getUserAbacRole(user) {
  return String(
    user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "") || ""
  )
    .toLowerCase()
    .trim();
}

export function isPrivilegedAbacBypassRole(user) {
  if (!user) return false;
  const role = getUserAbacRole(user);
  if (role === "tenant" || role.includes("tenant")) return false;
  return role === "superadmin" || role === "system";
}

/** Admin login always keeps Error Codes + Documents visible (ABAC later). */
export function isAdminRole(user) {
  return getUserAbacRole(user) === "admin";
}

/** ABAC UI/route gates run only for multi-access member sessions. */
export function shouldEnforceAbacForUser(user, profile, _permissions) {
  if (!user) return false;
  if (isPrivilegedAbacBypassRole(user)) return false;
  return isMultiAccessUser(user, profile);
}

export const SMS_DASHBOARD_GRAPH_ABAC = {
  msgStatus: ["message_status"],
  accountSummary: ["account_summary"],
  recentActivity: ["recent_activity"],
  countryWiseSub: ["operator_country"],
  senderWiseSub: ["sender_id"],
  userWiseSt: ["user_distribution"],
  smscWiseDvy: ["route_distribution"],
  smscWiseSub: ["route_percentage"],
  smscWiseSpam: ["smsc_spam"],
  userWiseSpam: ["user_spam"],
  performance: ["system_dashboard"],
  traffic: ["traffic_list"],
};

export const WHATSAPP_DASHBOARD_GRAPH_ABAC = {
  messageStatusDistribution: ["message_status_distribution"],
  liveCampaigns: ["campaign_data"],
  messageVolume: ["message_volume"],
  costing: ["cost_data"],
  templateUsage: ["template_usage"],
  templateApprovalStatus: ["template_approval_status"],
};

export const TELEGRAM_DASHBOARD_GRAPH_ABAC = {
  statusDist: ["message_status_distribution"],
  typeDist: ["message_type_distribution"],
  msgVolume: ["message_volume"],
  costing: ["cost_data"],
  templateUsage: ["template_usage_data", "template_usage"],
  campaigns: ["campaign_data"],
};

export const EMAIL_DASHBOARD_GRAPH_ABAC = {
  deliveryStatus: ["status_distribution"],
  emailVolume: ["volume_data"],
  costing: ["cost_data"],
  // One chart on Email Dashboard → one ABAC toggle (not paired with schedule_status_distribution).
  scheduledVsSend: ["scheduled_vs_send_data"],
  templateUsage: ["template_usage_data", "template_usage"],
  campaigns: ["campaign_data"],
};

export const SOCIAL_DASHBOARD_GRAPH_ABAC = {
  deliveryStatus: ["status_distribution"],
  outboundMix: ["type_distribution"],
  messageVolume: ["volume_data"],
  statusDist: ["status_distribution", "message_status_distribution"],
  typeDist: ["type_distribution", "message_type_distribution"],
  msgVolume: ["volume_data", "message_volume"],
  costing: ["cost_data"],
  templateUsage: ["template_usage_data", "template_usage"],
  campaigns: ["campaign_data"],
};

export const CHANNEL_DASHBOARD_GRAPH_ABAC = {
  sms: SMS_DASHBOARD_GRAPH_ABAC,
  whatsapp: WHATSAPP_DASHBOARD_GRAPH_ABAC,
  telegram: TELEGRAM_DASHBOARD_GRAPH_ABAC,
  email: EMAIL_DASHBOARD_GRAPH_ABAC,
  instagram: SOCIAL_DASHBOARD_GRAPH_ABAC,
  messenger: SOCIAL_DASHBOARD_GRAPH_ABAC,
  rcs: SOCIAL_DASHBOARD_GRAPH_ABAC,
  push: SOCIAL_DASHBOARD_GRAPH_ABAC,
};

function isDashboardWidgetEnabled(value) {
  if (value === true || value === 1 || value === "true") return true;
  if (value && typeof value === "object") {
    return value.view === true || value.view === 1 || value.view === "true";
  }
  return false;
}

function readDashboardWidgetValue(permissions, channel, key) {
  const smsWidgets = getAbacValueAtPath(permissions, [
    channel,
    "dashboard",
    "widgets",
  ]);
  const smsVal =
    smsWidgets &&
    typeof smsWidgets === "object" &&
    Object.prototype.hasOwnProperty.call(smsWidgets, key)
      ? smsWidgets[key]
      : undefined;

  const overviewKey = DASHBOARD_OVERVIEW_MERGE_KEYS.includes(key) ? key : null;
  const overviewWidgets = overviewKey
    ? getAbacValueAtPath(permissions, ["overview", "widgets"])
    : undefined;
  const overviewVal =
    overviewKey &&
    overviewWidgets &&
    typeof overviewWidgets === "object" &&
    Object.prototype.hasOwnProperty.call(overviewWidgets, overviewKey)
      ? overviewWidgets[overviewKey]
      : undefined;

  return { smsVal, overviewVal };
}

/** Merged tiles follow both overview.widgets and sms.dashboard.widgets (DU). */
function isMergedDashboardWidgetOn(permissions, channel, key) {
  const { smsVal, overviewVal } = readDashboardWidgetValue(
    permissions,
    channel,
    key
  );
  const sources = [smsVal, overviewVal].filter((value) => value !== undefined);
  if (!sources.length) return true;
  return sources.some(isDashboardWidgetEnabled);
}

function widgetKeysAllow(permissions, channel, keyList) {
  return (Array.isArray(keyList) ? keyList : [keyList]).some((key) =>
    isMergedDashboardWidgetOn(permissions, channel, key)
  );
}

function hasAnyEnabledDashboardWidgetNode(widgets) {
  if (!widgets || typeof widgets !== "object") return false;
  return Object.values(widgets).some(isDashboardWidgetEnabled);
}

export function canViewChannelDashboard(channel, permissions, user, profile) {
  const ch = resolveAbacChannelKey(channel);
  if (isPrivilegedAbacBypassRole(user)) return true;
  // Non multi-access users: Access Permissions do not apply.
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  // Multi-access but permissions not loaded yet → fail open until fetch finishes.
  if (permissions == null) return true;

  const view = getAbacValueAtPath(permissions, [ch, "dashboard", "view"]);
  // Hide only when View is explicitly OFF.
  if (view === false || view === "false" || view === 0) return false;

  const smsWidgets = getAbacValueAtPath(permissions, [
    ch,
    "dashboard",
    "widgets",
  ]);
  const overviewWidgets = getAbacValueAtPath(permissions, [
    "overview",
    "widgets",
  ]);
  const hasWidgetTree =
    (smsWidgets && typeof smsWidgets === "object") ||
    (overviewWidgets && typeof overviewWidgets === "object");
  if (hasWidgetTree) {
    const role = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");
    const allowed = getDashboardWidgetKeysForRole(role);
    if (allowed == null) {
      return (
        hasAnyEnabledDashboardWidgetNode(smsWidgets) ||
        hasAnyEnabledDashboardWidgetNode(overviewWidgets)
      );
    }
    return allowed.some((key) =>
      isMergedDashboardWidgetOn(permissions, ch, key)
    );
  }

  if (view === true || view === "true" || view === 1) return true;
  return true;
}

export function canViewChannelDashboardWidget(
  channel,
  permissions,
  graphId,
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (permissions == null) {
    return true;
  }
  if (!canViewChannelDashboard(ch, permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  const map = CHANNEL_DASHBOARD_GRAPH_ABAC[ch] || {};
  const keys = map[graphId];
  if (!keys) return true;
  return widgetKeysAllow(
    permissions,
    ch,
    Array.isArray(keys) ? keys : [keys]
  );
}

export function canViewChannelDashboardMetrics(
  channel,
  permissions,
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (permissions == null) {
    return true;
  }
  if (!canViewChannelDashboard(ch, permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  // KPI / summary visibility: SMS uses account_summary (merged with overview like DU).
  const metricKeys =
    ch === "sms"
      ? ["account_summary", "summary_metrics"]
      : ["metrics", "summary_metrics", "account_summary"];

  return metricKeys.some((key) =>
    isMergedDashboardWidgetOn(permissions, ch, key)
  );
}

/**
 * Whether channel dashboard KPI cards may show their percentage values.
 * Independent of Metrics row visibility; gated by widgets.metrics_percentage.
 */
export function canViewChannelDashboardMetricsPercentage(
  channel,
  permissions,
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (permissions == null) return true;
  if (!canViewChannelDashboard(ch, permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  const widgets = getAbacValueAtPath(permissions, [
    ch,
    "dashboard",
    "widgets",
  ]);
  if (!widgets || typeof widgets !== "object") return true;
  if (!Object.prototype.hasOwnProperty.call(widgets, "metrics_percentage")) {
    return true;
  }
  return hasAbacPermission(permissions, [
    ch,
    "dashboard",
    "widgets",
    "metrics_percentage",
  ]);
}

export function canViewSmsDashboard(permissions, user, profile) {
  return canViewChannelDashboard("sms", permissions, user, profile);
}

export function canViewSmsDashboardWidget(permissions, graphId, user, profile) {
  return canViewChannelDashboardWidget(
    "sms",
    permissions,
    graphId,
    user,
    profile
  );
}

export function canViewSmsDashboardMetrics(permissions, user, profile) {
  return canViewChannelDashboardMetrics("sms", permissions, user, profile);
}

/** Overview uses top-level `overview.view` (not channel dashboard.view). */
export function canViewOverviewDashboard(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const view = getAbacValueAtPath(permissions, ["overview", "view"]);
  if (view === false || view === "false" || view === 0) return false;
  if (view === true || view === "true" || view === 1) return true;
  return true;
}

export const OVERVIEW_WIDGET_ABAC = {
  accountSummary: ["account_summary"],
  recentActivity: ["recent_activity"],
  channelTraffic: ["channel_traffic"],
  userTraffic: ["user_traffic"],
  costing: ["cost_trend"],
  overallMetrics: ["overall_metrics"],
};

function overviewWidgetKeysAllow(permissions, keyList) {
  const widgets = getAbacValueAtPath(permissions, ["overview", "widgets"]);
  if (!widgets || typeof widgets !== "object") return true;

  const present = keyList.filter((key) =>
    Object.prototype.hasOwnProperty.call(widgets, key)
  );
  if (present.length === 0) return true;

  return present.some((key) =>
    hasAbacPermission(permissions, ["overview", "widgets", key])
  );
}

export function canViewOverviewWidget(
  permissions,
  graphId,
  user,
  profile
) {
  if (permissions == null) return true;
  if (!canViewOverviewDashboard(permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  const keys = OVERVIEW_WIDGET_ABAC[graphId];
  if (!keys) return true;
  return overviewWidgetKeysAllow(
    permissions,
    Array.isArray(keys) ? keys : [keys]
  );
}

export function canViewOverviewMetrics(permissions, user, profile) {
  return canViewOverviewWidget(
    permissions,
    "overallMetrics",
    user,
    profile
  );
}

/** True if any boolean leaf under node is true. */
export function hasAnyTruthyPermission(node) {
  if (node === true || node === 1 || node === "true") return true;
  if (node === false || node == null) return false;
  if (typeof node !== "object") return false;
  return Object.values(node).some((v) => hasAnyTruthyPermission(v));
}

/** API/UI leaf aliases (company.edit ↔ update, module.view ↔ list). Same as Omni. */
function abacLeafKeyAliases(key) {
  const k = String(key || "").trim();
  if (!k) return [];
  if (k === "BasicUpdate") return ["BasicUpdate", "basic"];
  if (k === "basic") return ["basic", "BasicUpdate"];
  if (k === "BasicView") return ["BasicView", "BasicUpdate", "basic"];
  if (k === "OptionalView") return ["OptionalView", "optional"];
  if (k === "optional") return ["optional", "OptionalView", "OptionalUpdate"];
  if (k === "OptionalUpdate") return ["OptionalUpdate", "optional"];
  if (k === "MMsView") return ["MMsView", "mms", "MmsView"];
  if (k === "mms") return ["mms", "MMsView", "MmsUpdate"];
  if (k === "MmsUpdate") return ["MmsUpdate", "mms"];
  if (k === "edit") return ["edit", "update"];
  if (k === "update") return ["update", "edit"];
  if (k === "view") return ["view", "list"];
  if (k === "list") return ["list", "view"];
  if (k === "balance_add") return ["balance_add", "add_balance", "balanceAdd"];
  if (k === "balance_deduct") {
    return ["balance_deduct", "deduct_balance", "balanceDeduct"];
  }
  return [k];
}

/**
 * Known company/vendor action leaves. When the module object is present but a
 * leaf key is omitted (APIs often drop `false`), treat as deny — otherwise
 * Balance Add OFF never hides the Add Balance button (fail-open on missing).
 */
const ABAC_FAIL_CLOSED_MODULE_LEAVES = {
  company: new Set([
    "view",
    "list",
    "create",
    "edit",
    "update",
    "delete",
    "balance_add",
    "balance_deduct",
    "add_balance",
    "deduct_balance",
    "balanceAdd",
    "balanceDeduct",
    "company_flag",
    "company_templates",
    "company_user_list",
    "transaction_history",
  ]),
  vendor: new Set([
    "view",
    "list",
    "create",
    "edit",
    "update",
    "delete",
    "view_smsc",
  ]),
};

/** True when a nested key is missing under an object the policy already sent (DU). */
function isMissingAbacChildUnderExistingParent(permissions, path) {
  if (!permissions || !path?.length) return false;
  let current = permissions;
  for (let i = 0; i < path.length; i++) {
    if (!current || typeof current !== "object") return i > 0;
    if (!Object.prototype.hasOwnProperty.call(current, path[i])) {
      return i > 0;
    }
    current = current[path[i]];
  }
  return false;
}

function getAbacValueWithLeafAliases(permissions, resolved) {
  const direct = getAbacValueAtPath(permissions, resolved);
  if (direct !== undefined) return direct;
  if (resolved.length < 2) return undefined;
  const last = resolved[resolved.length - 1];
  const parentPath = resolved.slice(0, -1);
  for (const alias of abacLeafKeyAliases(last)) {
    if (alias === last) continue;
    const alt = getAbacValueAtPath(permissions, [...parentPath, alias]);
    if (alt !== undefined) return alt;
  }
  return undefined;
}

/**
 * General ABAC gate. This is the only hide/show switch for wired UI.
 * - Normal login (not a multi-access member) → always allow (never hide)
 * - Privileged roles (superadmin/system) → always allow
 * - Permissions not loaded (null) → allow (fail open)
 * - Multi-access member + explicit false on leaf / `.view` → deny
 * - Company/Vendor known leaf omitted while module exists → deny (Omni)
 * - Other missing paths → allow
 */
export function canAccessAbac(permissions, path, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  if (!path?.length) return true;

  const segments = Array.isArray(path)
    ? path
    : String(path)
        .split(".")
        .map((s) => s.trim())
        .filter(Boolean);

  if (!segments.length) return true;

  const resolved = segments.map((seg, i) =>
    i === 0 ? resolveAbacChannelKey(seg) : seg
  );

  // Module master off (view:false) must hide every leaf instantly.
  if (resolved.length >= 2) {
    const moduleNode = getAbacValueAtPath(permissions, [resolved[0]]);
    if (
      moduleNode &&
      typeof moduleNode === "object" &&
      typeof moduleNode.view === "boolean" &&
      moduleNode.view === false
    ) {
      return false;
    }
  }

  const value = getAbacValueWithLeafAliases(permissions, resolved);
  if (value === undefined) {
    // Legacy path …/doc/view|download when doc is now a boolean leaf.
    const last = resolved[resolved.length - 1];
    if (last === "view" || last === "download" || last === "list") {
      const parent = getAbacValueAtPath(
        permissions,
        resolved.slice(0, -1)
      );
      if (typeof parent === "boolean") return parent;
    }
    // Company/Vendor: module present + known leaf missing → deny (omitted false).
    if (resolved.length === 2) {
      const moduleKey = resolved[0];
      const leafSet = ABAC_FAIL_CLOSED_MODULE_LEAVES[moduleKey];
      const moduleNode = getAbacValueAtPath(permissions, [moduleKey]);
      if (
        leafSet?.has(last) &&
        moduleNode &&
        typeof moduleNode === "object"
      ) {
        return false;
      }
    }
    // Parent object exists but this child was omitted (APIs drop `false`) → deny.
    if (
      resolved.length >= 2 &&
      isMissingAbacChildUnderExistingParent(permissions, resolved)
    ) {
      return false;
    }
    return true;
  }
  if (value === false || value === "false" || value === 0) return false;
  if (value === true || value === "true" || value === 1) return true;

  if (value && typeof value === "object") {
    if (typeof value.view === "boolean") return value.view;

    if (typeof value.list === "boolean" && value.view === undefined) {
      const actionKeys = Object.keys(value).filter(
        (k) => !String(k).startsWith("_")
      );
      if (actionKeys.length === 1 && actionKeys[0] === "list") {
        return value.list;
      }
      return hasAnyTruthyPermission(value);
    }
    return hasAnyTruthyPermission(value);
  }

  return true;
}

/** Allow if any of the given paths is allowed. */
export function canAccessAbacAny(permissions, paths, user, profile) {
  if (!paths?.length) return true;
  return paths.some((path) => canAccessAbac(permissions, path, user, profile));
}

const CHANNEL_KEYS = [
  "sms",
  "whatsapp",
  "telegram",
  "email",
  "instagram",
  "messenger",
  "rcs",
  "push",
];

function presentChannelPaths(permissions, leaf) {
  if (!permissions || typeof permissions !== "object") return [];
  return CHANNEL_KEYS.filter((ch) => permissions[ch] != null).map((ch) => [
    ch,
    leaf,
  ]);
}

/** Any channel_access.view across channels present in the tree. */
export function canAccessChannelsPage(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const paths = presentChannelPaths(permissions, "channel_access");
  if (!paths.length) return true;
  return canAccessAbacAny(permissions, paths, user, profile);
}

/**
 * Show a channel card on /channels when any channel_access leaf is true
 * (view and/or request). Missing channel_access → allow.
 */
export function canSeeChannelCard(permissions, channel, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const ch = resolveAbacChannelKey(channel);
  if (!ch) return true;
  const node = getAbacValueAtPath(permissions, [ch, "channel_access"]);
  if (node === undefined) return true;
  return hasAnyTruthyPermission(node);
}

/**
 * Use a channel on /channels when channel_access.view is allowed.
 * Missing channel_access → allow.
 */
export function canUseChannelAccess(permissions, channel, user, profile) {
  return canAccessAbac(
    permissions,
    [resolveAbacChannelKey(channel), "channel_access"],
    user,
    profile
  );
}

/**
 * Socials channel icon visibility for multi-access sessions.
 * Live status stays separate (API /me) — callers AND this with channelStatus.
 *
 * Same rule for every channel (SMS, WhatsApp, …):
 * - channel_access is NOT checked (it only gates the /channels page)
 * - if a `message` node exists, at least one Message leaf must be true
 *   (Message 0/N → hide that Socials icon)
 * - missing `message` key → allow (channels without that area)
 * - ABAC not loaded yet → deny (no fail-open flash for icons)
 */
export function canShowSocialsChannelIcon(
  permissions,
  channel,
  user,
  profile,
  { abacSessionLoaded = true } = {}
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (!abacSessionLoaded || permissions == null) return false;

  const ch = resolveAbacChannelKey(channel);
  if (!ch) return true;

  const moduleNode = getAbacValueAtPath(permissions, [ch]);
  if (moduleNode == null || typeof moduleNode !== "object") return true;

  // Same as SMS: Message off hides the Socials icon. Campaign / other areas
  // do not keep the icon visible when Message is 0/N.
  if (moduleNode.message === undefined) return true;
  return hasAnyTruthyPermission(moduleNode.message);
}

/**
 * Show "Request Access" on /channels when channel_access.request is allowed.
 * Missing request leaf → allow (fail open).
 */
export function canRequestChannelAccess(permissions, channel, user, profile) {
  return canAccessAbac(
    permissions,
    [resolveAbacChannelKey(channel), "channel_access", "request"],
    user,
    profile
  );
}

/** Any channel template leaf (view/create/update/delete/download/publish). */
export function canAccessTemplatesMenu(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const channels = presentChannelPaths(permissions, "template").map(
    (path) => path[0]
  );
  if (!channels.length) return true;
  return channels.some((ch) =>
    canAccessAnyChannelTemplateAction(permissions, ch, user, profile)
  );
}

/**
 * Per-channel template leaf: view | create | update | delete | download | publish.
 * Each leaf is independent — view off must not imply create/update/etc. are off.
 * Missing leaf → fail open (same as canAccessAbac).
 */
export function canAccessChannelTemplate(
  permissions,
  channel,
  action = "view",
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (!ch) return true;
  const leaf = String(action || "view").trim().toLowerCase() || "view";
  return canAccessAbac(permissions, [ch, "template", leaf], user, profile);
}

/** True if any template leaf is allowed for the channel (for tab visibility). */
export function canAccessAnyChannelTemplateAction(
  permissions,
  channel,
  user,
  profile
) {
  const actions = [
    "view",
    "create",
    "update",
    "delete",
    "download",
    "publish",
  ];
  return actions.some((action) =>
    canAccessChannelTemplate(permissions, channel, action, user, profile)
  );
}

/**
 * Omni Inbox sidebar / route: allow when any channel that *defines*
 * `omni_inbox` has View enabled. Channels without the node are ignored
 * (avoids fail-open from missing keys on other channels).
 * No omni_inbox nodes anywhere → fail open.
 */
export function canAccessOmniInbox(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const channelsWithInbox = CHANNEL_KEYS.filter(
    (ch) => getAbacValueAtPath(permissions, [ch, "omni_inbox"]) !== undefined
  );
  if (!channelsWithInbox.length) return true;
  return channelsWithInbox.some((ch) =>
    canAccessChannelOmniInbox(permissions, ch, user, profile)
  );
}

/**
 * Per-channel Omni Inbox tab (View leaf).
 * Missing `omni_inbox` on that channel → allow (leaf not rolled out yet).
 * Explicit false / view:false → deny.
 */
export function canAccessChannelOmniInbox(
  permissions,
  channel,
  user,
  profile
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const ch = resolveAbacChannelKey(channel);
  if (!ch) return true;
  const moduleNode = getAbacValueAtPath(permissions, [ch, "omni_inbox"]);
  if (moduleNode === undefined) return true;
  // Prefer explicit View leaf when present (matches Access Permissions UI).
  if (
    moduleNode &&
    typeof moduleNode === "object" &&
    typeof moduleNode.view === "boolean"
  ) {
    return canAccessAbac(
      permissions,
      [ch, "omni_inbox", "view"],
      user,
      profile
    );
  }
  return canAccessAbac(permissions, [ch, "omni_inbox"], user, profile);
}

/** Navbar / common helpers (flat tree after extractPermissionsFromResponse). */
export function canAccessNavbarItem(permissions, key, user, profile) {
  return canAccessAbac(permissions, ["navbar", key], user, profile);
}

/** Alias pairs for API snake_case vs UI camelCase action keys. */
function navbarActionKeyAliases(actionKey) {
  const key = String(actionKey ?? "").trim();
  if (!key) return [];
  const aliases = [key];
  if (key.includes("_")) {
    aliases.push(
      key.replace(/_([a-z])/g, (_, char) => String(char).toUpperCase())
    );
  } else if (/[A-Z]/.test(key)) {
    aliases.push(key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`));
  }
  return [...new Set(aliases)];
}

const NAVBAR_PROFILE_FAIL_CLOSED_LEAVES = new Set([
  "view",
  "edit",
  "update",
  "updatePassword",
  "update_password",
  "alertsAndNotifications",
  "alerts_and_notifications",
]);


export function canAccessNavbarPermission(
  permissions,
  itemKey,
  actionKey,
  user,
  profile
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const aliases = navbarActionKeyAliases(actionKey);
  let sawDefined = false;
  for (const alias of aliases) {
    const path = ["navbar", itemKey, alias];
    const value = getAbacValueAtPath(permissions, path);
    if (value === undefined) continue;
    sawDefined = true;
    if (canAccessAbac(permissions, path, user, profile)) return true;
  }
  if (sawDefined) return false;
  if (itemKey === "profile") {
    const node = getAbacValueAtPath(permissions, ["navbar", "profile"]);
    if (node && typeof node === "object") {
      return !NAVBAR_PROFILE_FAIL_CLOSED_LEAVES.has(String(actionKey || "").trim());
    }
  }
  return true;
}

export function canAccessWalletBalance(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const value = getAbacValueAtPath(permissions, ["navbar", "wallet_balance"]);
  if (value === undefined) {
    const wallet = getAbacValueAtPath(permissions, ["navbar", "wallet"]);
    if (wallet === undefined) return true;
    return canAccessAbac(permissions, ["navbar", "wallet"], user, profile);
  }
  return canAccessAbac(permissions, ["navbar", "wallet_balance"], user, profile);
}

export function canAccessWallet(permissions, user, profile) {
  return canAccessWalletBalance(permissions, user, profile);
}

export function canAccessWalletPage(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const wallet = getAbacValueAtPath(permissions, ["navbar", "wallet"]);
  if (wallet === undefined) {
    const rootWallet = getAbacValueAtPath(permissions, ["wallet"]);
    if (rootWallet === undefined) return true;
    return canAccessAbac(permissions, ["wallet"], user, profile);
  }
  return canAccessAbac(permissions, ["navbar", "wallet"], user, profile);
}

export function canAccessWalletDownload(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const path = getAbacValueAtPath(permissions, ["navbar", "wallet", "download"]) !== undefined
    ? ["navbar", "wallet", "download"]
    : ["wallet", "download"];
  if (getAbacValueAtPath(permissions, path) === undefined) return true;
  return canAccessAbac(permissions, path, user, profile);
}

export function canAccessWalletPricing(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const path =
    getAbacValueAtPath(permissions, ["navbar", "wallet", "pricing"]) !== undefined
      ? ["navbar", "wallet", "pricing"]
      : ["wallet", "pricing"];
  if (getAbacValueAtPath(permissions, path) === undefined) return true;
  return canAccessAbac(permissions, path, user, profile);
}

export function canAccessWalletPricingDownload(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  if (!canAccessWalletPricing(permissions, user, profile)) return false;
  const nested = getAbacValueAtPath(permissions, [
    "navbar",
    "wallet",
    "pricing",
    "download",
  ]);
  const root = getAbacValueAtPath(permissions, ["wallet", "pricing", "download"]);
  if (nested === undefined && root === undefined) {
    const pricing =
      getAbacValueAtPath(permissions, ["navbar", "wallet", "pricing"]) ??
      getAbacValueAtPath(permissions, ["wallet", "pricing"]);
    if (pricing && typeof pricing === "object") return false;
    return true;
  }
  const path = nested !== undefined
    ? ["navbar", "wallet", "pricing", "download"]
    : ["wallet", "pricing", "download"];
  return canAccessAbac(permissions, path, user, profile);
}

export function canAccessDefaultSetting(permissions, key, user, profile) {
  return canAccessAbac(permissions, ["default", key], user, profile);
}


export const ROUTING_EDIT_ACCESS_PATHS = [
  ["sms", "routing", "edit", "BasicView"],
  ["sms", "routing", "edit", "OptionalView"],
  ["sms", "routing", "edit", "MMsView"],
  ["sms", "routing", "edit", "export"],
  ["sms", "routing", "edit", "apply"],
  ["sms", "routing", "edit", "BasicUpdate"],
  ["sms", "routing", "edit", "OptionalUpdate"],
  ["sms", "routing", "edit", "MmsUpdate"],
  ["sms", "routing", "edit", "delete"],
  ["sms", "routing", "edit", "undo"],
  ["sms", "routing", "edit", "previous"],
];

export const ROUTING_COPY_ACCESS_PATHS = [
  ["sms", "routing", "copy", "view"],
  ["sms", "routing", "copy", "copy"],
];

export const ROUTING_ADD_ACCESS_PATHS = [
  ["sms", "routing", "add", "view"],
  ["sms", "routing", "add", "import"],
  ["sms", "routing", "add", "apply"],
  ["sms", "routing", "add", "create"],
];

export const ROUTING_SENDER_ACCESS_PATHS = [
  ["sms", "routing", "sender", "add", "view"],
  ["sms", "routing", "sender", "add", "create"],
  ["sms", "routing", "sender", "edit", "view"],
  ["sms", "routing", "sender", "edit", "update"],
];

export const ROUTING_MENU_ACCESS_PATHS = [
  ...ROUTING_ADD_ACCESS_PATHS,
  ...ROUTING_EDIT_ACCESS_PATHS,
  ...ROUTING_COPY_ACCESS_PATHS,
  ...ROUTING_SENDER_ACCESS_PATHS,
];

/** Nested `.view` only — leftover download/publish extras must not keep a module open. */
export function hasAnyTruthyView(node) {
  if (node === true || node === 1 || node === "true") return true;
  if (node === false || node == null) return false;
  if (typeof node !== "object") return false;
  if (Object.prototype.hasOwnProperty.call(node, "view")) {
    const view = node.view;
    if (view === true || view === 1 || view === "true") return true;
    if (
      view === false ||
      view === 0 ||
      view === "false" ||
      typeof view === "boolean"
    ) {
      return false;
    }
    return hasAnyTruthyView(view);
  }
  return Object.values(node).some(
    (value) => value && typeof value === "object" && hasAnyTruthyView(value)
  );
}

/**
 * MA fail-closed view gate. Missing path → deny. Non-MA / unloaded → allow.
 */
export function canAccessAbacView(permissions, path, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  if (!path?.length) return false;
  const resolved = path.map((segment, index) =>
    index === 0 ? resolveAbacChannelKey(segment) : segment
  );
  const value = getAbacValueWithLeafAliases(permissions, resolved);
  if (value === undefined) return false;
  if (value === false || value === "false" || value === 0) return false;
  if (value === true || value === "true" || value === 1) return true;
  return hasAnyTruthyView(value);
}

export const PLATFORM_DOC_API_CARD_ABAC = {
  hlrApi: "hlr_api",
  smsApi: "sms_api",
};

/** ApiRunner top-level folder name → cpaas_business_api.* */
export const PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC = {
  SMS: "sms_business_api",
  "LEGACY SMS": "sms_business_api",
};

export function getPlatformDocBusinessApiAbacKey(folderName) {
  const raw = String(folderName || "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC[upper]) {
    return PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC[upper];
  }
  const normalized = upper.replace(/\s+/g, " ");
  return PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC[normalized] || null;
}

export function getPlatformDocApiCardAbacKey(cardKey) {
  return PLATFORM_DOC_API_CARD_ABAC[cardKey] || null;
}

export function getPlatformDocApiCardAbacKeys(cardKey) {
  const key = getPlatformDocApiCardAbacKey(cardKey);
  return key ? [key] : [];
}


export const PLATFORM_DOC_FEATURE_MODULE_ABAC = {
  login: ["login"],
  common_functionality: ["common_functionality"],
  dashboard: ["dashboard"],
  documentation: ["documentation"],
  messaging: ["messaging"],
  socials: ["socials", "messaging"],
  sms_utilities: ["sms_utilities"],
  reports: ["reports"],
  address_book: ["address_book"],
  templates: ["templates"],
  approvals: ["approvals"],
  user: ["user"],
  routing: ["routing"],
  company: ["company"],
  vendor: ["vendor"],
  firewall: ["firewall"],
  smsc: ["smsc"],
  dlt: ["dlt"],
  sales_teams: ["sales_teams"],
  network: ["network"],
  error_code: ["error_code"],
  user_templates: ["user_templates"],
};

const PLATFORM_DOC_FEATURE_FILE_ABAC = {
  Login: "login",
  "Common Functionality": "common_functionality",
  Dashboard: "dashboard",
  Documentation: "documentation",
  Messaging: "messaging",
  Utilities: "sms_utilities",
  Reports: "reports",
  "Address Book": "address_book",
  Templates: "templates",
  Approvals: "approvals",
  User: "user",
  View: "user",
  Routing: "routing",
  Company: "company",
  Vendor: "vendor",
  Firewall: "firewall",
  SMSC: "smsc",
  DLT: "dlt",
  Error_Code: "error_code",
  "User Template": "user_templates",
  "Sales Team": "sales_teams",
  Network: "network",
};

export function canAccessPlatformDoc(permissions, docKey, action, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const key = String(docKey || "").trim();
  if (!key) return false;
  const node = getAbacValueAtPath(permissions, [
    "platform_documents",
    "cpaas_documentation",
    "api_documents",
    key,
  ]);
  if (node === undefined) return false;
  if (typeof node === "boolean") {
    return action === "download" ? false : node === true;
  }
  if (!node || typeof node !== "object") return false;
  const view = node.view === true;
  if (action === "download") return view && node.download === true;
  return view;
}

function isFeatureDocLeafOn(value) {
  return value === true || value === "true" || value === 1;
}

function hasAnyBackendFeatureDocOn(features, role) {
  if (typeof features === "boolean") return features === true;
  if (!features || typeof features !== "object") return false;

  return Object.entries(features).some(([key, value]) => {
    if (isLeftoverFeatureDocKey(key)) return false;
    if (!isFeatureDocKeyAllowedForRole(role, key)) return false;
    return isFeatureDocLeafOn(value);
  });
}

export function canAccessPlatformDocFeature(
  permissions,
  featureKey,
  user,
  profile
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const role = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");

  const features = getAbacValueAtPath(permissions, [
    "platform_documents",
    "cpaas_documentation",
    "features_documents",
  ]);

  if (typeof features === "boolean") return features === true;

  const resolvedKey = resolveFeatureDocKeyForRole(role, featureKey);

  if (isLeftoverFeatureDocKey(resolvedKey)) {
    return hasAnyBackendFeatureDocOn(features, role);
  }

  if (features && typeof features === "object") {
    const aliases =
      PLATFORM_DOC_FEATURE_MODULE_ABAC[resolvedKey] || [resolvedKey];

    const present = aliases.filter((key) =>
      Object.prototype.hasOwnProperty.call(features, key)
    );


    if (!present.length) {
      return (
        isFeatureDocKeyAllowedForRole(role, resolvedKey) &&
        hasAnyBackendFeatureDocOn(features, role)
      );
    }

    return present.some((key) => isFeatureDocLeafOn(features[key]));
  }

  return false;
}

export function canAccessPlatformDocApiCard(
  permissions,
  cardKey,
  user,
  profile
) {
  const keys = getPlatformDocApiCardAbacKeys(cardKey);
  if (!keys.length) return true;
  return canAccessAbacAny(
    permissions,
    keys.map((key) => [
      "platform_documents",
      "cpaas_documentation",
      "api_documents",
      key,
    ]),
    user,
    profile
  );
}

export function canViewPlatformDocumentsUi(
  permissions,
  user,
  profile
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const role = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");

  const featureKeys = [
    ...new Set(Object.values(PLATFORM_DOC_FEATURE_FILE_ABAC)),
  ];

  return featureKeys.some((featureKey) => {
    const resolvedKey = resolveFeatureDocKeyForRole(role, featureKey);

    if (isLeftoverFeatureDocKey(resolvedKey)) return false;

    if (!isFeatureDocKeyAllowedForRole(role, featureKey)) return false;

    return canAccessPlatformDocFeature(
      permissions,
      featureKey,
      user,
      profile
    );
  });
}

export function canAccessPlatformDocumentsPage(permissions, user, profile) {
  return canViewPlatformDocumentsUi(permissions, user, profile);
}

export function canAccessFeatureDocument(permissions, fileName, user, profile) {
  const featureKey = PLATFORM_DOC_FEATURE_FILE_ABAC[fileName] || fileName;
  return canAccessPlatformDocFeature(permissions, featureKey, user, profile);
}

export function getPlatformDocFeatureAbacKey(moduleName) {
  return PLATFORM_DOC_FEATURE_FILE_ABAC[moduleName] || null;
}
